/**
 * ClaimDiscoveryCTA — public acquisition banner.
 *
 * Shown ONLY on pages where the university is NOT claimed. Encourages
 * anonymous university staff who happen to be reading the page to
 * claim it and start verifying. Complements the `VerifyMoreNudge`
 * (which is only for already-claimed owners).
 *
 * Guards:
 *   - Hide if `claimed === true` (someone else already owns it)
 *   - Hide if current user is already a claim-owner for THIS uni
 *     (they'd see the VerifyMoreNudge instead)
 *   - Dismissible per session (sessionStorage keyed by uni id)
 *
 * Fetches `GET /claims/uni/{university_id}` — public endpoint, safe
 * to call unauthenticated.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, ArrowRight, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ClaimDiscoveryCTA({ universityId, universityName }) {
  const { user } = useAuth();
  const [claimed, setClaimed] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    try {
      const key = `wua_claim_cta_dismissed_${universityId}`;
      if (sessionStorage.getItem(key) === "1") setDismissed(true);
    } catch { /* ignore */ }
  }, [universityId]);

  useEffect(() => {
    let cancelled = false;
    if (!universityId) return;
    (async () => {
      try {
        const { data } = await api.get(`/claims/uni/${universityId}`);
        if (!cancelled) setClaimed(!!data?.claimed);
      } catch {
        if (!cancelled) setClaimed(null);
      }
    })();
    return () => { cancelled = true; };
  }, [universityId]);

  useEffect(() => {
    let cancelled = false;
    if (!user || !universityId) {
      setIsOwner(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/claims/mine");
        const owned = (data.items || []).some(
          (c) => c.status === "approved" && c.university_id === universityId,
        );
        if (!cancelled) setIsOwner(owned);
      } catch {
        if (!cancelled) setIsOwner(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, universityId]);

  const show =
    claimed === false &&
    !dismissed &&
    !isOwner;

  if (!show) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(`wua_claim_cta_dismissed_${universityId}`, "1");
    } catch { /* ignore */ }
  };

  const shortName = universityName || "this university";

  return (
    <div
      className="mt-4 mb-6 border border-purple-300 bg-purple-50/70 rounded-lg px-4 py-3 flex items-center gap-3"
      data-testid="claim-discovery-cta"
    >
      <GraduationCap className="h-5 w-5 text-purple-800 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-purple-900">
          <strong className="font-semibold">
            Are you from {shortName}?
          </strong>{" "}
          Claim this page and verify data directly — takes 2 minutes
          and adds a green tick next to every field you confirm.
        </div>
      </div>
      <Link
        to={`/uni/${universityId}/claim`}
        className="text-[12px] font-underground-sc tracking-wider text-white bg-purple-800 hover:bg-purple-900 rounded px-3 py-1.5 inline-flex items-center gap-1 shrink-0"
        data-testid="claim-discovery-cta-link"
      >
        Claim this page <ArrowRight className="h-3 w-3" />
      </Link>
      <button
        type="button"
        onClick={handleDismiss}
        className="text-stone-400 hover:text-stone-700 shrink-0"
        data-testid="claim-discovery-cta-dismiss"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
